import _ from 'lodash';
import React, { memo, useContext, useMemo } from 'react';
import { Accordion, Divider, Icon, Tag, Typography } from '@scale/scaleui';
import IssueGroupAccordion from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/components/IssueGroupAccordion';
import { useContributorFeedbackStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/stores/useContributorFeedbackStore';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { ContributorFeedbackStepContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/context';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { ReviewLevel } from '@scale/shared-types';
import { twMerge } from 'tailwind-merge';
import { OVERALL_FEEDBACK_ID } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/constants';
import {
  ChatTheme,
  useChatThemeStore,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useChatThemeStore';

interface FeedbackHeaderProps {
  className?: string;
  numUnaddressedFeedback: number;
}

const FeedbackHeader: React.FC<FeedbackHeaderProps> = ({ className, numUnaddressedFeedback }) => {
  return (
    <div className={twMerge('flex flex-row justify-between items-center rounded-t-md', className)}>
      <div className="flex flex-row py-1.5 gap-1 items-center">
        <Icon variant="black" icon={['fas', 'message-pen']} />

        <div className="flex flex-row items-center gap-2">
          <Typography.Subtitle4 className="text-base">Reviewer feedback</Typography.Subtitle4>
          <Typography.Subtitle4 className="text-base text-neutral-500">
            Overall task
          </Typography.Subtitle4>
          {!!numUnaddressedFeedback && (
            <Tag className="bg-danger-50" size="sm">
              <Typography.Paragraph3 className="text-danger-500">
                {numUnaddressedFeedback} unresolved
              </Typography.Paragraph3>
            </Tag>
          )}
        </div>
      </div>
    </div>
  );
};

interface ContributorOverallFeedbackBoxProps {
  className?: string;
}

const ContributorOverallFeedbackBox: React.FC<ContributorOverallFeedbackBoxProps> = ({
  className,
}) => {
  const taskContext = useContext(ChatTaskContext);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const theme = useChatThemeStore(state => state.theme);

  const overallTaskFeedback = useContributorFeedbackStore(s =>
    s.feedback.filter(({ childId }) => !childId),
  );

  const [myFeedback, existingFeedback] = useMemo(
    () =>
      _.partition(
        overallTaskFeedback,
        feedbackItem => feedbackItem.author && feedbackItem.author === taskContext.workerId,
      ),
    [overallTaskFeedback, taskContext.workerId],
  );

  const isReviewer = reviewLevel !== ReviewLevel.Attempt;

  const numUnaddressedFeedbackItems = useMemo(
    () => existingFeedback.filter(feedbackItem => !feedbackItem.action).length,
    [existingFeedback],
  );

  const shouldShowMyOverallComments = isReviewer && !!myFeedback.length;

  // Darkmode support is built-in for standard `bg-` tailwind classes (i.e bg-neutral-200).
  // Since we're using custom colors, we need to handle darkmode manually.
  const bgColor = theme === ChatTheme.Light ? 'bg-[#F7FAFF]' : 'bg-[#101217]';

  return (
    <>
      {!!overallTaskFeedback.length && (
        // Overall task feedback does not have a defined step, so we don't need to provide a stepWithIndexKey
        <ContributorFeedbackStepContext.Provider value={{ stepWithIndexKey: undefined }}>
          <div
            id={OVERALL_FEEDBACK_ID}
            className={twMerge(
              'flex flex-col rounded-md border border-neutral-200 overflow-hidden',
              className,
            )}
          >
            <Accordion
              className={bgColor}
              contentContainerClassName="p-0"
              buttonClassName="py-1.5 -mx-2"
              iconClassName="-mr-2"
              header={<FeedbackHeader numUnaddressedFeedback={numUnaddressedFeedbackItems} />}
              target={
                <div>
                  {/* An attempter won't be able to see their OWN feedback even if they attempt an L-1 task that they left a review on */}
                  {shouldShowMyOverallComments && (
                    <IssueGroupAccordion
                      accordionClassName={bgColor}
                      headerTitle="My overall comments"
                      areUnaddressedFeedbackItems={false}
                      feedbackItems={myFeedback}
                      defaultExpanded={false}
                    />
                  )}
                  {!!existingFeedback.length && (
                    <IssueGroupAccordion
                      accordionClassName={bgColor}
                      // HACK: Header div isn't show if the `headerTitle` is an empty string
                      headerTitle={
                        isReviewer && shouldShowMyOverallComments
                          ? 'Overall comments from other reviewers'
                          : ''
                      }
                      areUnaddressedFeedbackItems={!!numUnaddressedFeedbackItems}
                      feedbackItems={existingFeedback}
                      defaultExpanded={false}
                    />
                  )}
                </div>
              }
              startOpen
            />
          </div>
          <Divider className="mt-4" />
        </ContributorFeedbackStepContext.Provider>
      )}
    </>
  );
};

export default memo(ContributorOverallFeedbackBox);
